'use client';

import * as React from 'react';
import * as TabsPrimitive from '@radix-ui/react-tabs';
import {cn} from '@purpose/ui/lib/utils';

const Tabs = TabsPrimitive.Root;

const TabsList = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.List>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.List>
>(({className, ...props}, ref) => (
  <TabsPrimitive.List
    ref={ref}
    className={cn(
      'text-muted-foreground inline-flex h-10 items-center justify-start rounded-md border-b border-gray-200 bg-transparent p-0',
      className
    )}
    {...props}
  />
));
TabsList.displayName = TabsPrimitive.List.displayName;

const TabsTrigger = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.Trigger>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.Trigger>
>(({className, ...props}, ref) => (
  <TabsPrimitive.Trigger
    ref={ref}
    className={cn(
      'ring-offset-background focus-visible:ring-ring inline-flex items-center justify-center rounded-t-sm px-4 py-2 text-sm font-medium whitespace-nowrap transition-all focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none disabled:pointer-events-none disabled:opacity-50 data-[state=active]:border-b-2 data-[state=active]:border-gray-900 data-[state=active]:bg-white data-[state=active]:text-gray-900 data-[state=inactive]:bg-transparent data-[state=inactive]:text-gray-500',
      className
    )}
    {...props}
  />
));
TabsTrigger.displayName = TabsPrimitive.Trigger.displayName;

const TabsContent = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.Content>
>(({className, ...props}, ref) => (
  <TabsPrimitive.Content
    ref={ref}
    className={cn(
      'ring-offset-background focus-visible:ring-ring mt-2 focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none',
      className
    )}
    {...props}
  />
));
TabsContent.displayName = TabsPrimitive.Content.displayName;

// MUI-compatible wrapper component
interface TabsProps {
  tabs: {
    title: string;
    children: React.ReactNode;
    renderWrapper?: (
      children: React.ReactNode,
      key: string | number
    ) => React.ReactNode;
  }[];
  idPrefix: string;
  tab?: number;
  setTab?: (v: number) => void;
  containerClassName?: string;
}

const MuiCompatibleTabs = ({
  tabs,
  idPrefix,
  tab,
  setTab,
  containerClassName
}: TabsProps) => {
  const [value, setValue] = React.useState(0);

  const handleChange = (newValue: string) => {
    if (setTab) setTab(parseInt(newValue));
    else setValue(parseInt(newValue));
  };

  return (
    <div className={cn('w-full', containerClassName)}>
      <Tabs
        value={(tab || value).toString()}
        onValueChange={handleChange}
        className="h-full w-full"
      >
        <TabsList className="w-full">
          {tabs.map((item, index) =>
            item?.renderWrapper ? (
              item.renderWrapper(
                <TabsTrigger
                  key={index}
                  value={index.toString()}
                  className="min-w-40 text-sm font-medium"
                  id={`simple-tab-${idPrefix}-${index}`}
                  aria-controls={`simple-tabpanel-${idPrefix}-${index}`}
                >
                  {item.title}
                </TabsTrigger>,
                item.title
              )
            ) : (
              <TabsTrigger
                key={index}
                value={index.toString()}
                className="min-w-40 text-sm font-medium"
                id={`simple-tab-${idPrefix}-${index}`}
                aria-controls={`simple-tabpanel-${idPrefix}-${index}`}
              >
                {item.title}
              </TabsTrigger>
            )
          )}
        </TabsList>
        {tabs.map((item, index) => (
          <TabsContent
            key={`panel-${item.title}`}
            value={index.toString()}
            className="mt-0 h-full"
          >
            <div
              role="tabpanel"
              id={`simple-tabpanel-${idPrefix}-${index}`}
              aria-labelledby={`simple-tab-${idPrefix}-${index}`}
              className="h-full p-6"
            >
              {item.children}
            </div>
          </TabsContent>
        ))}
      </Tabs>
    </div>
  );
};

export {
  Tabs as TabsPrimitive,
  TabsList,
  TabsTrigger,
  TabsContent,
  MuiCompatibleTabs as Tabs
};
export type {TabsProps};
